import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Undo2, Trash2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLLECTION_LABELS = {
  clients: 'Client',
  projects: 'Project',
  tasks: 'Task',
  client_reports: 'Client Report',
  invoices: 'Invoice',
  team_members: 'Team Member',
  transactions: 'Transaction',
  postponed_payments: 'Postponed Payment',
};

const TrashPage = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('cse_token')}` });

  const fetchTrash = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/trash`, { headers: authHeader() });
      setBatches(res.data);
    } catch {
      toast.error('Failed to load trash');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTrash(); }, [fetchTrash]);

  const restoreBatch = async (batchId, label) => {
    try {
      await axios.post(`${API}/trash/${batchId}/restore`, {}, { headers: authHeader() });
      toast.success(`Restored: ${label}`);
      window.dispatchEvent(new CustomEvent('app:trash-updated'));
      fetchTrash();
    } catch {
      toast.error('Failed to restore');
    }
  };

  const purgeBatch = async (batchId, label) => {
    try {
      await axios.delete(`${API}/trash/${batchId}`, { headers: authHeader() });
      toast.success(`Permanently deleted: ${label}`);
      window.dispatchEvent(new CustomEvent('app:trash-updated'));
      fetchTrash();
    } catch {
      toast.error('Failed to purge');
    }
  };

  const purgeAll = async () => {
    try {
      await axios.delete(`${API}/trash`, { headers: authHeader() });
      toast.success('Trash emptied');
      window.dispatchEvent(new CustomEvent('app:trash-updated'));
      fetchTrash();
    } catch {
      toast.error('Failed to empty trash');
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('id-ID', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen p-6" data-testid="trash-page">
      <div className="max-w-5xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/hub')} className="mb-4" data-testid="back-to-hub">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Hub
        </Button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Trash / Recycle Bin
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              Anything you deleted lands here first. Restore with 1 click or permanently purge.
            </p>
          </div>

          {batches.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-red-600 hover:bg-red-50 border-red-200" data-testid="empty-trash-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Empty All
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Permanently delete everything in trash?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This cannot be undone. {batches.length} recoverable {batches.length === 1 ? 'item' : 'items'} will be lost forever.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={purgeAll} className="bg-red-600 hover:bg-red-700">
                    Yes, empty trash
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {loading ? (
          <p className="text-slate-500">Loading...</p>
        ) : batches.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <Trash2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">Trash is empty. Nothing to recover.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {batches.map((b) => (
              <Card key={b.batchId} data-testid={`trash-batch-${b.batchId}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base truncate">{b.batchLabel}</CardTitle>
                      <p className="text-xs text-slate-500 mt-1">Deleted {fmtDate(b.deletedAt)}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => restoreBatch(b.batchId, b.batchLabel)}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`restore-${b.batchId}`}
                      >
                        <Undo2 className="w-4 h-4 mr-1.5" /> Restore
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`purge-${b.batchId}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Permanently delete this item?</AlertDialogTitle>
                            <AlertDialogDescription>
                              <strong>{b.batchLabel}</strong> will be lost forever. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => purgeBatch(b.batchId, b.batchLabel)} className="bg-red-600 hover:bg-red-700">
                              Yes, delete forever
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="text-xs text-slate-500 space-y-0.5">
                    {b.items.map((it, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="inline-block px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-mono uppercase text-slate-600">
                          {COLLECTION_LABELS[it.sourceCollection] || it.sourceCollection}
                        </span>
                        <span className="truncate">
                          {it.documentSnapshot?.name
                            || it.documentSnapshot?.title
                            || it.documentSnapshot?.description
                            || it.documentSnapshot?.invoiceNumber
                            || it.documentSnapshot?.teamMemberName
                            || it.documentSnapshot?.clientName
                            || it.originalId}
                        </span>
                      </div>
                    ))}
                  </div>
                  {b.items.some((it) => (it.documentSnapshot?.sideEffects || []).length > 0) && (
                    <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>Restoring this will also reverse related balance changes (amountPaid, invoice status, etc.).</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrashPage;
